import React, {useState, useEffect} from 'react';
import {
    Lock,
    Eye,
    EyeOff,
    CheckCircle,
    AlertCircle,
    ArrowRight,
    Home,
    Loader,
    Check,
    X,
    Shield,
    Key,
    Clock
} from 'lucide-react';
import {useNavigate, useSearchParams} from "react-router-dom";
import {API_CONSTANTS} from "../../Constants";
import SEO from "../../Components/Common/SEO";

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState(null); // null, 'success', 'error', 'expired'
    const [errors, setErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState({score: 0, feedback: []});

    // There's no separate token-validation endpoint; an expired/unknown token
    // is only discovered when /reset-password is actually submitted. A missing
    // token (e.g. the link's query param got stripped) can be caught upfront.
    useEffect(() => {
        if (!token) {
            setStatus('expired');
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsLoading(true);
        setStatus(null);
        setErrors({});
        try {
            const response = await fetch(API_CONSTANTS.BASE_URL + '/auth/reset-password', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    token,
                    password: formData.password,
                    confirmPassword: formData.confirmPassword
                })
            });
            const data = await response.json();
            if (data.success) {
                setStatus('success');
                setTimeout(() => navigate('/login'), 2000);
            } else if (data.error === 'Invalid or expired reset token') {
                setStatus('expired');
            } else if (data.field) {
                setErrors({[data.field]: data.message});
            } else {
                setStatus('error');
                setErrors({general: data.message || data.error || 'Failed to reset password.'});
            }
        } catch (error) {
            setStatus('error');
            setErrors({general: 'Failed to reset password. Please try again.'});
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Check password strength
        if (name === 'password') {
            checkPasswordStrength(value);
        }
    };

    const checkPasswordStrength = (password) => {
        const checks = [
            {test: password.length >= 8, message: 'At least 8 characters'},
            {test: /[A-Z]/.test(password), message: 'One uppercase letter'},
            {test: /[a-z]/.test(password), message: 'One lowercase letter'},
            {test: /\d/.test(password), message: 'One number'},
            {test: /[!@#$%^&*]/.test(password), message: 'One special character'}
        ];

        const score = checks.filter(check => check.test).length;
        const feedback = checks.map(check => ({
            message: check.message,
            valid: check.test
        }));

        setPasswordStrength({score, feedback});
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (passwordStrength.score < 5) {
            newErrors.password = 'Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const getPasswordStrengthColor = () => {
        if (passwordStrength.score <= 2) return 'bg-murram-500';
        if (passwordStrength.score <= 3) return 'bg-dawn-500';
        return 'bg-highland-500';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength.score <= 2) return 'Weak';
        if (passwordStrength.score <= 3) return 'Medium';
        return 'Strong';
    };

    // Expired/Invalid Token State
    if (status === 'expired') {
        return (
            <div className="min-h-screen bg-karibu-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <SEO title="Reset Password" canonicalPath="/reset-password" noindex />
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-murram-100/40 to-dawn-100/40"/>
                </div>

                <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="text-center">
                        <div
                            className="mx-auto h-16 w-16 bg-murram-500 rounded-2xl flex items-center justify-center mb-6">
                            <Clock className="h-8 w-8 text-white"/>
                        </div>
                        <h2 className="font-display text-3xl font-bold text-soil-900">Link Expired</h2>
                        <p className="mt-2 text-sm text-soil-600">
                            This password reset link has expired or is invalid.
                        </p>
                    </div>
                </div>

                <div className="relative z-10 mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow-xl rounded-xl sm:px-10">
                        <div className="text-center">
                            <div
                                className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-murram-100/40 mb-6">
                                <AlertCircle className="h-8 w-8 text-murram-500"/>
                            </div>

                            <h3 className="font-display text-lg font-medium text-soil-900 mb-2">Reset Link Expired</h3>
                            <p className="text-sm text-soil-600 mb-6">
                                Password reset links expire 1 hour after they're sent for security reasons.
                                Please request a new reset link to continue.
                            </p>

                            <div className="space-y-4">
                                <button
                                    onClick={() => navigate('/forgot-password')}
                                    className="w-full bg-highland-500 hover:bg-highland-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                                >
                                    Request New Reset Link
                                </button>

                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full bg-soil-100 hover:bg-soil-100/70 text-soil-600 font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                                >
                                    Back to Login
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <SEO title="Reset Password" canonicalPath="/reset-password" noindex />
            {/* Background Pattern */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-highland-50 to-karibu-200"/>
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }}/>
            </div>

            <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-md">
                {/* Header */}
                <div className="text-center">
                    <div
                        className="mx-auto h-16 w-16 bg-highland-500 rounded-2xl flex items-center justify-center mb-6">
                        <Key className="h-8 w-8 text-white"/>
                    </div>
                    <h2 className="font-display text-3xl font-bold text-soil-900">Reset Password</h2>
                    <p className="mt-2 text-sm text-soil-600">
                        Create a new password for your account
                    </p>
                </div>
            </div>

            <div className="relative z-10 mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow-xl rounded-xl sm:px-10">
                    {/* Success State */}
                    {status === 'success' ? (
                        <div className="text-center">
                            <div
                                className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-highland-50 mb-6">
                                <CheckCircle className="h-8 w-8 text-highland-500"/>
                            </div>

                            <h3 className="font-display text-lg font-medium text-soil-900 mb-2">Password Reset Successfully!</h3>
                            <p className="text-sm text-soil-600 mb-6">
                                Your password has been updated. You can now sign in with your new password.
                            </p>

                            <div className="bg-highland-50 border border-highland-100 rounded-lg p-4 mb-6">
                                <div className="flex items-center">
                                    <Shield className="h-5 w-5 text-highland-500 mr-3"/>
                                    <div className="text-left">
                                        <h4 className="text-sm font-medium text-highland-700 mb-1">Security Reminder:</h4>
                                        <p className="text-sm text-highland-600">Keep your password secure and don't share
                                            it with anyone.</p>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/login')}
                                className="w-full bg-highland-500 hover:bg-highland-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                            >
                                Continue to Login
                            </button>

                            <p className="mt-4 text-sm text-soil-400">
                                Redirecting automatically in 3 seconds...
                            </p>
                        </div>
                    ) : (
                        /* Form State */
                        <div>
                            {/* Error Message */}
                            {status === 'error' && errors.general && (
                                <div
                                    className="mb-6 p-4 bg-murram-100/40 border border-murram-100 rounded-lg flex items-center space-x-3">
                                    <AlertCircle className="w-5 h-5 text-murram-500"/>
                                    <p className="text-murram-600">{errors.general}</p>
                                </div>
                            )}

                            <div className="space-y-6">
                                {/* New Password */}
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-soil-600">
                                        New Password
                                    </label>
                                    <div className="mt-1 relative">
                                        <div
                                            className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Lock className="h-5 w-5 text-soil-400"/>
                                        </div>
                                        <input
                                            id="password"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent ${
                                                errors.password ? 'border-murram-500' : 'border-soil-100'
                                            }`}
                                            placeholder="Create a strong password"
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5 text-soil-400"/>
                                            ) : (
                                                <Eye className="h-5 w-5 text-soil-400"/>
                                            )}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="mt-1 text-sm text-murram-600">{errors.password}</p>
                                    )}

                                    {/* Password Strength Indicator */}
                                    {formData.password && (
                                        <div className="mt-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-soil-600">Password strength:</span>
                                                <span className={`text-sm font-medium ${
                                                    passwordStrength.score <= 2 ? 'text-murram-600' :
                                                        passwordStrength.score <= 3 ? 'text-dawn-600' : 'text-highland-600'
                                                }`}>
                                                    {getPasswordStrengthText()}
                                                </span>
                                            </div>
                                            <div className="w-full bg-soil-100 rounded-full h-2 mb-3">
                                                <div
                                                    className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                                                    style={{width: `${(passwordStrength.score / 5) * 100}%`}}
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 gap-1">
                                                {passwordStrength.feedback.map((item, index) => (
                                                    <div key={index} className="flex items-center text-sm">
                                                        {item.valid ? (
                                                            <Check className="w-3 h-3 text-highland-500 mr-2"/>
                                                        ) : (
                                                            <X className="w-3 h-3 text-murram-500 mr-2"/>
                                                        )}
                                                        <span
                                                            className={item.valid ? 'text-highland-600' : 'text-murram-600'}>
                                                            {item.message}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label htmlFor="confirmPassword"
                                           className="block text-sm font-medium text-soil-600">
                                        Confirm New Password
                                    </label>
                                    <div className="mt-1 relative">
                                        <div
                                            className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Lock className="h-5 w-5 text-soil-400"/>
                                        </div>
                                        <input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent ${
                                                errors.confirmPassword ? 'border-murram-500' : 'border-soil-100'
                                            }`}
                                            placeholder="Confirm your new password"
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? (
                                                <EyeOff className="h-5 w-5 text-soil-400"/>
                                            ) : (
                                                <Eye className="h-5 w-5 text-soil-400"/>
                                            )}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && (
                                        <p className="mt-1 text-sm text-murram-600">{errors.confirmPassword}</p>
                                    )}

                                    {/* Password Match Indicator */}
                                    {formData.confirmPassword && (
                                        <div className="mt-2 flex items-center text-sm">
                                            {formData.password === formData.confirmPassword ? (
                                                <>
                                                    <Check className="w-4 h-4 text-highland-500 mr-2"/>
                                                    <span className="text-highland-600">Passwords match</span>
                                                </>
                                            ) : (
                                                <>
                                                    <X className="w-4 h-4 text-murram-500 mr-2"/>
                                                    <span className="text-murram-600">Passwords don't match</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <button
                                    onClick={handleSubmit}
                                    disabled={isLoading}
                                    className="w-full bg-highland-500 hover:bg-highland-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader className="w-5 h-5 animate-spin"/>
                                            <span>Resetting Password...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Reset Password</span>
                                            <ArrowRight className="w-5 h-5"/>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Security Notice */}
                {status !== 'success' && (
                    <div className="mt-6 bg-highland-50 border border-highland-100 rounded-lg p-4">
                        <div className="flex items-start">
                            <Shield className="h-5 w-5 text-highland-500 mt-0.5 mr-3"/>
                            <div>
                                <h4 className="text-sm font-medium text-highland-700 mb-1">Security Tips:</h4>
                                <ul className="text-sm text-highland-600 space-y-1">
                                    <li>• Use a unique password you haven't used before</li>
                                    <li>• Include a mix of letters, numbers, and symbols</li>
                                    <li>• Avoid using personal information</li>
                                    <li>• Consider using a password manager</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* Back to Home */}
                <div className="mt-6 text-center">
                    <button
                        onClick={() => navigate('/')}
                        className="inline-flex items-center text-sm text-soil-400 hover:text-soil-600"
                    >
                        <ArrowRight className="w-4 h-4 mr-1 rotate-180"/>
                        Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;